import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { CardItem } from "../../common/Card/Card";
import { CardBody } from "../../common/Card/CardBody";
import CardGroup from '../../common/Card/CardGroup';
import CardGroupSlider from '../../common/Card/CardGroupSlider';
import Carousel, { CarouselItem } from '../../common/Carousel';
import CssLoader from "../../components/cssloader/CssLoader";
import Link from '../../common/Link';
import Tab, { PanelItem } from '../../common/Tab';
import Table from '../../common/Table';
import { homeMatchsColumns, homeMatchsMobileColumns } from '../../config/Tables/Home';
import { ArticleCategory, HomeMatchCategory, HomeMatchCategoryGroup, HomeTodayMatch, iHome } from "../../helpers/interfaces/home";
import { VideosList, VideoItem } from "../../helpers/interfaces/video";
import { CategoryGroup, CategoryMatch } from '../../helpers/interfaces/category';
import { MatchItem, MatchStatus } from '../../helpers/interfaces/match';
import { FacebookIcon, InstagramIcon, TwitterIcon, WhatsAppIcon } from '../../components/Icons';
import { Card, Col, Row } from 'react-bootstrap';
import { HTMLElement } from '../../common/HTMLElement';
import Advertising from '../../common/Advertising';
import { homeService } from '../../services/HomeService';
import Sidebar from "../../components/Sidebar";
import { useMenuSingleton } from '../../singleton/menuSingleton';
import moment from 'moment';
import 'moment/locale/it';
import "./Home.css";

async function createArticleArray(
    articoli: ArticleCategory[]
): Promise<CardItem[]> {
    let data: CardItem[] = [];

    articoli && articoli.length && articoli.forEach((articolo: ArticleCategory) => {
        data.push({
            id: articolo.id,
            title: articolo.titolo,
            subtitle: <>{articolo.strDataPubblicazione} - <span className="author-name">{articolo.nomeAutore}</span></>,
            content: null,
            image: articolo.img1,
            link: '/articolo/' + articolo.id,
            idCategoria: articolo.idCategoria,
            societa: articolo.societa
        })
    })

    return data;
};

function CardArticleBody(card: CardItem): ReactNode {
    let { image, altImageText, title, subtitle, content, link, linkTitle } = card;

    return <>
        <Link
            href={link}
            title={linkTitle}
        >
            <div className="card-image">
                {image && <img src={image} alt={altImageText} />}
            </div>
            {title && <Card.Title>{title}</Card.Title>}
            {subtitle && <Card.Subtitle>{subtitle}</Card.Subtitle>}
        </Link>
        <div className="card-text">{content}</div>
    </>
}

export default function HomePage() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [slideshow, setSlideshow] = useState<string | undefined>(undefined);
    const [carouselArticles, setCarouselArticles] = useState<Array<CarouselItem | never>>([]);
    const [highlightsNews, setHighlightsNews] = useState<Array<CardItem>>([]);
    const [highlightsVideos, setHighlightsVideos] = useState<Array<CardItem>>([]);
    const [categoryPanel, setCategoryPanel] = useState<number>(0);
    const [groupPanel, setGroupPanel] = useState<number>(0);
    const [groupMatches, setGroupMatches] = useState<MatchItem[]>([]);
    const [matchCategories, setMatchCategories] = useState<HomeMatchCategory[]>([]);
    const [categoryNews, setCategoryNews] = useState<{ [key: string]: Array<CardItem> }>({});
    const baseUrl = document.location.protocol + '//' + document.location.host
    const { getMenu, getSlugPath } = useMenuSingleton();

    useEffect(() => {
        (async () => {
            setIsLoading(true);

            const home: iHome | undefined = await homeService.getHome();

            let slideshow = undefined;
            let carousel: CarouselItem[] = [];
            let highlightsNews: CardItem[] = [];
            let categoryNews: { [key: string]: Array<CardItem> } = {};

            // slideshow = '<img src="https://picsum.photos/id/17/1280/720" alt="Slideshow" />'
            setSlideshow(slideshow);

            if (home) {
                home.articoliSlider.forEach((articolo) => {
                    carousel.push({
                        interval: 3000,
                        image: articolo.img1,
                        title: articolo.titolo,
                        subTitle: articolo.articoloBreve,
                        info: articolo.nomeAutore + ' - ' + articolo.strDataPubblicazione,
                        link: '/articolo/' + articolo.id
                    })
                });

                home.articoliInEvidenza.length && home.articoliInEvidenza.forEach((
                    articolo: ArticleCategory,
                    index: number,
                ) => {
                    const articoloUrl = baseUrl + '/articolo/' + articolo.id;

                    highlightsNews.push({
                        id: index,
                        content: <div className="card-overlay">
                            <a href={'/articolo/' + articolo.id} title={articolo.titolo}>
                                <div className='card-overlay-title'>
                                    {articolo.titolo}
                                </div>
                                <div className='card-overlay-subtitle'>
                                    <>
                                        {articolo.strDataPubblicazione} - <span className="author-name">{articolo.nomeAutore}</span>
                                    </>
                                </div>
                            </a>
                            <div className="card-overlay-share">
                                <a
                                    className="social-media-svg"
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articoloUrl)}`}
                                    target="_blank"
                                    title="Condividi su Facebook"
                                    rel="noopener noreferrer"
                                >
                                    <FacebookIcon />
                                </a>
                                <a
                                    className="social-media-svg"
                                    href={`https://www.instagram.com/?url=${encodeURIComponent(articoloUrl)}`}
                                    target="_blank"
                                    title="Condividi su Instagram"
                                    rel="noopener noreferrer"
                                >
                                    <InstagramIcon />
                                </a>
                                <a
                                    className="social-media-svg"
                                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent((articolo?.titolo ?? '') + ' ' + articoloUrl)}`}
                                    target="_blank"
                                    title="Condividi su WhatsApp"
                                    rel="noopener noreferrer"
                                >
                                    <WhatsAppIcon />
                                </a>
                                <a
                                    className="social-media-svg"
                                    href={`https://twitter.com/share?url=${encodeURIComponent(articoloUrl)}&text=${encodeURIComponent(articolo.titolo ?? '')}`}
                                    target="_blank"
                                    title="Condividi su Twitter"
                                    rel="noopener noreferrer"
                                >
                                    <TwitterIcon />
                                </a>
                            </div>
                        </div>,
                        image: articolo.img1,
                        link: '/articolo/' + articolo.id,
                        societa: []
                    })
                })

                setCarouselArticles(carousel);
                setHighlightsNews(highlightsNews);


                Object.keys(home.articoliPerCategorie).length > 0 && Object.keys(home.articoliPerCategorie).forEach(async (key: string) => {
                    categoryNews[key] = await createArticleArray(home.articoliPerCategorie[key]);
                });

                setCategoryNews(categoryNews);
            }

            const videos: VideosList | undefined = await homeService.getVideosList();

            const videoCards: CardItem[] = videos ? videos.videos.map((
                video: VideoItem,
                index: number
            ) => {
                return {
                    interval: 3000,
                    id: video.id,
                    title: video.name,
                    subtitle: <small>{moment(video.created_at).format('DD/MM/YYYY H:m:s')}</small>,
                    image: video.img,
                    content: <></>,
                    html: video.script ? video.script : '',
                    societa: [],
                    link: '/video/'+video.id
                }
            }) : [];

            setHighlightsVideos(videoCards);

            const todayMatchsList: HomeTodayMatch[] | undefined = await homeService.getTodayMatchs(
                moment().format('L')
            );

            let matchCategoriesList: HomeMatchCategory[] = [];

            if (todayMatchsList !== undefined) {
                todayMatchsList.forEach((matchCategory: HomeTodayMatch) => {
                    matchCategoriesList.push({
                        id: matchCategory.idCategoria,
                        name: matchCategory.nomeCategoria + ` (${matchCategory.numeroPartite})`,
                        count: matchCategory.numeroPartite,
                        groups: matchCategory.gironi.map((matchGroup: CategoryGroup) => {
                            return {
                                id: matchGroup.idGirone,
                                name: matchGroup.nomeGirone + ` (${matchGroup.numeroPartite})`,
                                count: matchGroup.numeroPartite,
                                matchs: matchGroup.partite.map((match: CategoryMatch) => {
                                    let status: MatchStatus = 'da-giocare';

                                    if (match.rinviata === '1') {
                                        status = 'rinviata';
                                    }

                                    if (match.inCorso === '1') {
                                        status = 'in-corso';
                                    }

                                    if (match.sospesa === '1') {
                                        status = 'sospesa';
                                    }

                                    if (match.chiusa === '1') {
                                        status = 'terminata';
                                    }

                                    return {
                                        status,
                                        time: `${match.hh}:${match.mm}`,
                                        team1: {
                                            id: match.idSquadraA,
                                            name: match.nomeA
                                        },
                                        team2: {
                                            id: match.idSquadraB,
                                            name: match.nomeB
                                        },
                                        score: match.risultato
                                    }
                                })
                            }
                        })
                    })
                });

                setMatchCategories(matchCategoriesList);
            }

            setIsLoading(false);
        })();
    }, [baseUrl]);

    const getMatchs = useCallback(async (
        categoryId: string,
        groupId?: string
    ) => {
        let matchItems: MatchItem[] = [];

        if (matchCategories.length > 0) {
            matchCategories.forEach((category: HomeMatchCategory) => {
                if (category.id === categoryId) {
                    if (
                        category.groups &&
                        category.groups.length > 0
                    ) {
                        if (!groupId) {
                            matchCategories && category.groups.length > 0 && matchCategories.forEach((
                                homeMatchCategory
                            ) => {
                                if (homeMatchCategory.id === categoryId) {
                                    groupId = homeMatchCategory.groups[0].id
                                }
                            });
                        }

                        groupId && category.groups.length > 0 && category.groups.forEach((
                            group: HomeMatchCategoryGroup
                        ) => {
                            if (group.id === groupId) {
                                group.matchs.forEach((match) => {
                                    matchItems.push(match);
                                });
                            }
                        })
                    }
                }
            })
        }

        setGroupMatches(matchItems);
    }, [matchCategories]);

    useEffect(() => {
        if (matchCategories && matchCategories.length > 0) {
            getMatchs(matchCategories[0].id);
        }
    }, [matchCategories, getMatchs]);

    let panels: PanelItem[] = [];
    let countNews = 0;

    matchCategories.length && matchCategories.forEach((
        category: HomeMatchCategory, index: number
    ) => {
        let groupPanels: PanelItem[] = [];

        if (category.name === 'Vari' || category.name === 'Video') {
            return false;
        }

        category.groups?.length && category.groups.forEach((
            group: HomeMatchCategoryGroup
        ) => {
            groupPanels.push({
                id: `${category.id}-${group.id}`,
                name: group.name,
                element: <div className="home-table-group">
                    <h3 className="text-center mt-3 group-title">{group.name}</h3>
                    <Table
                        id={`home-table-${category.id}-${group.id}`}
                        columns={homeMatchsColumns}
                        columnsMobile={homeMatchsMobileColumns}
                        data={groupMatches}
                        emptyDataText="Nessuna partita odierna presente"
                    />
                </div>
            });
        });

        panels.push({
            id: category.id,
            name: category.name,
            element: <React.Fragment key={index}>
                <h3 className="mt-3">{category.name}</h3>
                {groupPanels?.length ? (
                    <Tab
                        id={`home-tabs-${category.id}`}
                        activeKey={groupPanels[groupPanel] && groupPanels[groupPanel].id}
                        panels={groupPanels}
                        onChange={async (index: number) => {
                            setGroupPanel(index);
                            await getMatchs(
                                category.id, groupPanels[index].id.split('-')[1]
                            );
                        }}
                    />
                ) : (
                    <Table
                        id={`home-table-${category.id}`}
                        columns={homeMatchsColumns}
                        columnsMobile={homeMatchsMobileColumns}
                        data={groupMatches}
                        emptyDataText="Nessuna partita odierna presente"
                    />
                )}
            </React.Fragment>
        })
    });

    let countCat = 0;

    return <>
        {typeof slideshow !== 'undefined' ? <HTMLElement
            id="home-slideshow"
            as="div"
            className="slideshow"
            html={slideshow}
        /> : <Carousel
            id="home-carousel"
            items={carouselArticles}
            prevIcon={
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.5134 32.6123L40.377 46.4568C42.4031 47.6322 45 46.2106 45 43.8457V16.1567C45 13.7918 42.4031 12.3701 40.377 13.5455L16.5134 27.39C14.4955 28.5607 14.4955 31.4416 16.5134 32.6123Z" fill="#F9F9F9" />
                </svg>
            }
            nextIcon={
                <svg width="30" height="34" viewBox="0 0 30 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28.4866 19.6124L4.62297 33.4569C2.59689 34.6324 1.90735e-06 33.2107 1.90735e-06 30.8458L1.90735e-06 3.15678C1.90735e-06 0.791906 2.59689 -0.629783 4.62297 0.545655L28.4866 14.3902C30.5045 15.5608 30.5045 18.4418 28.4866 19.6124Z" fill="#F9F9F9" />
                </svg>
            }
        />}
        <h2>Articoli in evidenza</h2>
        <div id="home-highlights-news">
            <CardGroup
                id="home-highlights-news"
                cards={highlightsNews}
                columns={4}
                cardBody={CardArticleBody}
            />
        </div>
        {/* <div className="text-end">
            <Link href="/articoli" title="Video">Visualizza tutti gli articoli</Link>
        </div> */}
        <hr />
        <h2>Video in evidenza</h2>
        <CardGroupSlider
            id="home-highlights-video"
            cards={highlightsVideos}
            columns={4}
            cardBody={CardBody}
        />
        <div className="text-end">
            <Link href="/video" title="Video">Visualizza tutti i video</Link>
        </div>
        <hr />
        <Row id="page_content">
            <Col id="content" xs={12} lg={9}>
                {groupMatches.length > 0 && (
                    <>
                        <h2>Partite odierne - {moment().format('DD/MM/YYYY')}</h2>
                        <Tab
                            id="tab-partite-odierne"
                            title="Categoria:"
                            activeKey={panels[categoryPanel] && panels[categoryPanel].id}
                            panels={panels}
                            onChange={async (index: number) => {
                                setCategoryPanel(index)
                                setGroupPanel(0);
                                await getMatchs(panels[index].id);
                            }}
                        />
                        <div id="tab-partite-odierne-caption">
                            <i className="fa fa-circle rinviata" aria-hidden="true" /> = rinviata &nbsp;
                            <i className="fa fa-circle in-corso" aria-hidden="true" /> = In Corso &nbsp;
                            <i className="fa fa-circle sospesa" aria-hidden="true" /> = Sospesa &nbsp;
                            <i className="fa fa-circle terminata" aria-hidden="true" /> = Terminata &nbsp;
                            <i className="fa fa-circle-o da-giocare" aria-hidden="true" /> = Da giocare &nbsp;
                        </div>
                        <div className="text-end">
                            <Link href="/partite-odierne" title="Partite odierne">Visualizza tutte le partite</Link>
                        </div>
                        <hr />
                    </>
                )}
                <Advertising code="banner-topnews" as="div" />
                <div id="home-news-cards">
                    {Object.keys(categoryNews).length > 0 ? Object.keys(categoryNews).map((
                        categoryName: string,
                        index: number
                    ) => {
                        countNews++

                        const slugPath = getSlugPath(getMenu().data);
                        const keyCategory = categoryNews[categoryName][0].idCategoria;

                        const categoryPath = slugPath["_articoli_" + keyCategory];

                        if (countNews % 2 === 0) {
                            countCat++;
                        }

                        return categoryNews[categoryName] ? <>
                            <div key={countNews} className="home-news-card">
                                <h3>{categoryName}</h3>
                                <CardGroup
                                    id={`home-news-${index}`}
                                    columns={3}
                                    cards={categoryNews[categoryName] ?? []}
                                    cardBody={CardBody}
                                />
                                <div className="text-end">
                                    <Link href={`${categoryPath}`}>Tutte le notizie</Link>
                                    {countNews < Object.keys(categoryNews).length ? <hr /> : <></>}
                                </div>
                            </div>
                            {countNews % 2 === 0 && countNews < Object.keys(categoryNews).length
                                ? <Advertising
                                    code={'banner-cat' + countCat}
                                    as="div"
                                    className='text-center py-4'
                                /> : <></>
                            }
                        </> : <React.Fragment key={countNews}></React.Fragment>
                    }) : <></>}
                </div>
            </Col>
            <Col id="sidebar" xs={12} lg={3} className="pt-3">
                <Sidebar />
            </Col>
        </Row>

        <CssLoader active={isLoading} text="Caricamento della home page in corso..." />
    </>
}